'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { getVariantImageShapeClassName, ProductType } from '@headless-workspace/domain/common/client';
import { CapacityFormat } from '@headless-workspace/domain/common/server';
import { GlowIcon, GlowPalette, GlowTag, GlowTextBody, Icons } from '@headless-workspace/glow-ds';
import { GlowSelectorImage } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { FunctionHelpers } from '@headless-workspace/utils';
import { VariantSelector, VariantSelectorProps } from './VariantSelector';

export const VariantSelectorZone: Component<VariantSelectorProps> = ({
    type,
    sku,
    variants,
    formats,
    currency,
    enableDiscountInfo,
    priceAlwaysBlack,
    squaredShade,
    locale,
    fontFamily,
    productsSetList,
    isVipBrand,
}) => {
    const t = useTranslations('Discover.Pdp.information.selector');

    const Selector = useMemo(() => {
        return (
            <VariantSelector
                type={type}
                sku={sku}
                variants={variants}
                formats={formats}
                currency={currency}
                enableDiscountInfo={enableDiscountInfo}
                priceAlwaysBlack={priceAlwaysBlack}
                squaredShade={squaredShade}
                locale={locale}
                fontFamily={fontFamily}
                productsSetList={productsSetList}
                isVipBrand={isVipBrand}
            />
        );
    }, [
        currency,
        enableDiscountInfo,
        fontFamily,
        formats,
        locale,
        priceAlwaysBlack,
        sku,
        squaredShade,
        type,
        variants,
        productsSetList,
        isVipBrand,
    ]);

    const Palette = useMemo(() => {
        if (type !== ProductType.MULTI_SKU_SHADE_UNI && type !== ProductType.MULTI_SKU_SHADE_DIFF) {
            return null;
        }
        const shapeClassName = getVariantImageShapeClassName(type, squaredShade);

        return (
            <GlowPalette
                items={variants.map((variant) => ({
                    id: variant.id,
                    ariaLabel: t('accessibilityText.variant', {
                        variantName: variant.name,
                    }),
                    image: variant.thumbnailImage,
                    isAvailable: variant.isAvailable,
                }))}
                onClick={FunctionHelpers.emptyFn}
                renderItem={({ id, image, isAvailable }) => {
                    const isSelected = id === sku;

                    return (
                        <div
                            className={clsx(
                                'relative inline-block w-variantImage h-variantImage',
                                shapeClassName,
                                isSelected &&
                                    'border-bold border-solid border-border-selected p-paletteSelectedImg overflow-hidden',
                            )}
                        >
                            <GlowSelectorImage
                                image={image}
                                isAvailable={isAvailable}
                                isSelected={isSelected}
                                shapeClassName={shapeClassName}
                                squaredShade={squaredShade}
                            />

                            {!isAvailable && (
                                <div className={'absolute inset-0 flex items-end'}>
                                    <GlowIcon Icon={Icons.OOSBarGroup} className={'w-full h-full'} />
                                </div>
                            )}
                        </div>
                    );
                }}
                previousButtonAriaLabel={t('accessibilityText.prevBtn')}
                nextButtonAriaLabel={t('accessibilityText.nextBtn')}
            />
        );
    }, [type, variants, sku, squaredShade, t]);

    if (type === ProductType.PRODUCT_SET || type === ProductType.MONO_SKU) {
        return Selector;
    }

    const labelMap = {
        [ProductType.MULTI_SKU_SIZE]: t('label.selectMultiSkuSize', {
            variantCount: variants.length,
        }),
        [ProductType.MULTI_SKU_SHADE_UNI]: t('label.selectMultiSkuShade', {
            variantCount: variants.length,
        }),
        [ProductType.MULTI_SKU_SHADE_DIFF]: t('label.selectMultiSkuShade', {
            variantCount: variants.length,
        }),
    };

    return (
        <div className={'flex flex-col gap-0.75'}>
            <GlowTextBody text={labelMap[type]} fontFamily={fontFamily} />
            {Selector}
            {formats.includes(CapacityFormat.Mini) && (
                <GlowTag
                    label={t('label.availableInMiniFormat')}
                    type={'neutral'}
                    size={'medium'}
                    fontFamily={fontFamily}
                />
            )}
            {Palette}
        </div>
    );
};
